import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { X, Copy, CheckCircle, Loader, Clock, Smartphone, RefreshCw } from 'lucide-react';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../theme/netflixTheme';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface PixPaymentModalProps {
  visible: boolean;
  onClose: () => void;
  onPaymentConfirmed?: (sessionUuid: string) => void;
  planId: string;
  planPrice: number;
}

export function PixPaymentModal({
  visible,
  onClose,
  onPaymentConfirmed,
  planId,
  planPrice,
}: PixPaymentModalProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [pixData, setPixData] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  // Parâmetros de teste:
  // ?expiresIn=60 para usar 60 segundos em vez de 600
  // ?amount=50 para pagar R$ 50 em vez do preço padrão
  const debugExpiresIn = searchParams.get('expiresIn') ? parseInt(searchParams.get('expiresIn')!, 10) : 600;
  const debugAmount = searchParams.get('amount') ? parseFloat(searchParams.get('amount')!) : planPrice;

  const [pollCount, setPollCount] = useState(0);
  const MAX_POLLS = 30;
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollInitCountRef = useRef(0);

  const pollPaymentStatus = useCallback((sessionUuid: string) => {
    // Limpar intervalo anterior se existir
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    let currentPollCount = 0;

    const checkPayment = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payment/pix/consult/${sessionUuid}`
        );

        currentPollCount++;
        setPollCount(currentPollCount);

        if (response.ok) {
          const data = await response.json();
          // data.data pode ser uma string ("paid") ou um objeto com .status
          const status = typeof data.data === 'string' ? data.data : data.data?.status;

          if (status === 'paid' || status === 'completed') {
            setPaymentConfirmed(true);
            toast.success('Pagamento confirmado!');
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

            // Se usuário já está autenticado, ativar assinatura
            if (isAuthenticated) {
              try {
                const userData = JSON.parse(localStorage.getItem('user') || '{}');
                await fetch(
                  `${process.env.REACT_APP_API_URL}/payment/activate-subscription/${sessionUuid}`,
                  {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: userData.id }),
                  }
                );
              } catch {
                // Handle error silently
              }

              setTimeout(() => {
                onPaymentConfirmed?.(sessionUuid);
                onClose();
              }, 2000);
            } else {
              // Se não autenticado, redirecionar para signup com orderUUID
              setTimeout(() => {
                onClose();
                navigate('/plans', { state: { pendingPaymentId: sessionUuid, showSignup: true } });
              }, 1500);
            }
          }
        }

        // Para de fazer polling automático após 30 requisições
        if (currentPollCount >= MAX_POLLS) {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        }
      } catch {
        // Handle error silently
      }
    };

    // Contar quantas vezes o polling foi iniciado
    pollInitCountRef.current++;

    // Primeira consulta imediata
    checkPayment();

    // Depois, consulta a cada 5 segundos
    pollIntervalRef.current = setInterval(checkPayment, 5000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [onPaymentConfirmed, onClose, isAuthenticated, navigate]);

  const generatePix = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');

      const body = {
        email: userData.email || 'cliente@sekai.com.br',
        name: userData.name || userData.email?.split('@')[0] || 'Cliente',
        phone: userData.phone || '11999999999',
        document: userData.document || '12345678900',
        planName: planId || 'Premium',
        amount: debugAmount || 15,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payment/charge`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify(body),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPixData({
          sessionUuid: data.payment?.uuid || data.payment?.id,
          pixCode: data.payment?.qrcode_text || '',
          pixQrCode: data.payment?.qrcode || '',
          amount: debugAmount,
          expiresIn: debugExpiresIn,
        });
        toast.success('PIX gerado com sucesso!');
      } else {
        const error = await response.json();
        toast.error(error.message || error.errors?.[0]?.message || 'Erro ao gerar PIX', { style: { zIndex: 999999 } });
        onClose();
      }
    } catch {
      toast.error('Erro ao conectar com o servidor', { style: { zIndex: 999999 } });
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, [planId, debugAmount, debugExpiresIn, onClose]);

  useEffect(() => {
    if (visible && !pixData) {
      generatePix();
    }
  }, [visible, pixData, generatePix]);

  useEffect(() => {
    if (pixData?.sessionUuid && visible && !paymentConfirmed) {
      const cleanup = pollPaymentStatus(pixData.sessionUuid);
      return cleanup;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixData?.sessionUuid, visible, paymentConfirmed]);

  useEffect(() => {
    if (!pixData || paymentConfirmed || !visible) return;

    const timerInterval = setInterval(() => {
      setPixData((prev: any) => ({
        ...prev,
        expiresIn: Math.max(0, prev.expiresIn - 1)
      }));
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [pixData, paymentConfirmed, visible]);

  const copyPixCode = () => {
    if (pixData?.pixCode) {
      navigator.clipboard.writeText(pixData.pixCode);
      setCopied(true);
      toast.success('PIX copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const [manualCheckCooldown, setManualCheckCooldown] = useState(0);

  const handleManualCheck = async () => {
    if (manualCheckCooldown > 0) {
      toast.error(`Aguarde ${manualCheckCooldown}s antes de verificar novamente`);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payment/pix/consult/${pixData.sessionUuid}`
      );

      setPollCount((prev) => prev + 1);

      if (response.ok) {
        const data = await response.json();
        const status = typeof data.data === 'string' ? data.data : data.data?.status;

        if (status === 'paid' || status === 'completed') {
          setPaymentConfirmed(true);
          toast.success('Pagamento confirmado! 🎉');

          // Ativar assinatura no backend
          try {
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            await fetch(
              `${process.env.REACT_APP_API_URL}/payment/activate-subscription/${pixData.sessionUuid}`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: userData.id }),
              }
            );
            console.log('✅ Assinatura ativada');
          } catch (err) {
            console.error('❌ Erro ao ativar assinatura:', err);
          }

          setTimeout(() => {
            onPaymentConfirmed?.(pixData.sessionUuid);
            onClose();
          }, 2000);
          return;
        }
      }

      toast.loading('Pagamento ainda não confirmado');
      setManualCheckCooldown(5);
    } catch (err) {
      console.error('Erro ao verificar:', err);
      toast.error('Erro ao verificar pagamento');
    }
  };

  useEffect(() => {
    if (manualCheckCooldown > 0) {
      const timer = setTimeout(() => setManualCheckCooldown(manualCheckCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [manualCheckCooldown]);

  if (!visible) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <button style={closeButtonStyle} onClick={onClose}>
          <X size={24} color={NETFLIX_COLORS.textSecondary} />
        </button>

        <div style={contentStyle}>
          {isLoading ? (
            <div style={loadingContainerStyle}>
              <Loader size={56} color={NETFLIX_COLORS.primary} style={loaderSpinStyle} />
              <h3 style={loadingTitleStyle}>Gerando seu PIX...</h3>
              <p style={loadingSubtitleStyle}>Apenas um momento</p>
            </div>
          ) : paymentConfirmed ? (
            <div style={successContainerStyle}>
              <div style={successIconStyle}>
                <CheckCircle size={64} color={NETFLIX_COLORS.primary} strokeWidth={1.5} />
              </div>
              <h3 style={successTitleNewStyle}>Pagamento Confirmado! 🎉</h3>
              <p style={successSubtitleStyle}>Seu acesso foi liberado com sucesso</p>
              <p style={successHintStyle}>Redirecionando em breve...</p>
            </div>
          ) : pixData ? (
            <>
              <div style={priceCardStyle}>
                <p style={priceLabelStyle}>Valor a Pagar</p>
                <p style={priceValueStyle}>R$ {debugAmount.toFixed(2)}</p>
              </div>

              <div style={stepIndicatorStyle}>
                <div style={stepStyle}>
                  <div style={stepNumberStyle}>1</div>
                  <p style={stepTextStyle}>Escaneie o QR</p>
                </div>
                <div style={stepDividerStyle} />
                <div style={stepStyle}>
                  <div style={stepNumberStyle}>2</div>
                  <p style={stepTextStyle}>Confirme</p>
                </div>
                <div style={stepDividerStyle} />
                <div style={stepStyle}>
                  <div style={stepNumberStyle}>3</div>
                  <p style={stepTextStyle}>Pronto!</p>
                </div>
              </div>

              <div style={qrcodeContainerNewStyle}>
                <img
                  src={pixData.pixQrCode}
                  alt="PIX QR Code"
                  style={qrcodeImageStyle}
                />
              </div>

              <p style={orTextStyle}>OU COPIE O CÓDIGO</p>

              <div style={pixCodeBoxStyle}>
                <div style={pixCodeLeftStyle}>
                  <Smartphone size={20} color={NETFLIX_COLORS.primary} />
                  <div>
                    <p style={pixCodeLabelNewStyle}>Código PIX</p>
                    <p style={pixCodeValueNewStyle}>
                      {pixData.pixCode?.substring(0, 25)}...
                    </p>
                  </div>
                </div>
                <button style={copyButtonNewStyle} onClick={copyPixCode}>
                  <Copy size={20} color={NETFLIX_COLORS.white} />
                  <span>{copied ? 'Copiado!' : 'Copiar'}</span>
                </button>
              </div>

              {pollCount >= MAX_POLLS ? (
                <div style={manualCheckContainerStyle}>
                  <button
                    style={{
                      ...manualCheckButtonStyle,
                      ...(manualCheckCooldown > 0 && {
                        opacity: 0.6,
                        cursor: 'not-allowed',
                      }),
                    }}
                    onClick={handleManualCheck}
                    disabled={manualCheckCooldown > 0}
                  >
                    <RefreshCw size={16} />
                    <span>
                      {manualCheckCooldown > 0
                        ? `Aguarde ${manualCheckCooldown}s`
                        : 'Verificar Pagamento'}
                    </span>
                  </button>
                  <p style={checkCounterStyle}>
                    Verificações realizadas: {pollCount}
                  </p>
                </div>
              ) : (
                <div style={timerBoxStyle}>
                  <Clock size={16} color={NETFLIX_COLORS.primary} />
                  <span style={timerTextStyle}>Válido por {Math.floor(pixData.expiresIn / 60)}:{String(pixData.expiresIn % 60).padStart(2, '0')}</span>
                </div>
              )}

              <p style={bottomHintStyle}>⚠️ Não feche esta janela até confirmar o pagamento</p>
            </>
          ) : null}
        </div>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: '50px',
  zIndex: 10000,
  backdropFilter: 'blur(4px)',
};

const modalStyle: React.CSSProperties = {
  backgroundColor: NETFLIX_COLORS.surface,
  borderRadius: 16,
  width: '95%',
  maxWidth: 480,
  maxHeight: '80vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${NETFLIX_COLORS.border}`,
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
};

const contentStyle: React.CSSProperties = {
  padding: `${NETFLIX_SPACING.xl * 1.5}px`,
  overflowY: 'auto',
  flex: 1,
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: `${NETFLIX_SPACING.md}px`,
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 10,
};

const loadingContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 30px',
  gap: '16px',
};

const loadingTitleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: '700',
  color: NETFLIX_COLORS.textPrimary,
  margin: 0,
};

const loadingSubtitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: NETFLIX_COLORS.textSecondary,
  margin: 0,
};

const successContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '50px 30px',
  gap: '16px',
  textAlign: 'center',
};

const successIconStyle: React.CSSProperties = {
  marginBottom: '10px',
};

const successSubtitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: NETFLIX_COLORS.textSecondary,
  margin: 0,
};

const successHintStyle: React.CSSProperties = {
  fontSize: 12,
  color: NETFLIX_COLORS.primary,
  margin: '10px 0 0 0',
};

const priceCardStyle: React.CSSProperties = {
  backgroundColor: `${NETFLIX_COLORS.primary}15`,
  border: `2px solid ${NETFLIX_COLORS.primary}`,
  padding: '20px',
  borderRadius: '12px',
  textAlign: 'center',
  marginBottom: '30px',
};

const priceLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: '600',
  color: NETFLIX_COLORS.textSecondary,
  textTransform: 'uppercase',
  margin: '0 0 8px 0',
  letterSpacing: '0.5px',
};

const priceValueStyle: React.CSSProperties = {
  fontSize: 40,
  fontWeight: '700',
  color: NETFLIX_COLORS.primary,
  margin: 0,
};

const stepIndicatorStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '30px',
  gap: '10px',
};

const stepStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
};

const stepNumberStyle: React.CSSProperties = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  backgroundColor: NETFLIX_COLORS.primary,
  color: NETFLIX_COLORS.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: '700',
  fontSize: '16px',
};

const stepTextStyle: React.CSSProperties = {
  fontSize: 11,
  color: NETFLIX_COLORS.textSecondary,
  margin: 0,
  textAlign: 'center',
};

const stepDividerStyle: React.CSSProperties = {
  flex: 1,
  height: '2px',
  backgroundColor: `${NETFLIX_COLORS.border}`,
  margin: '0 -5px',
};

const qrcodeContainerNewStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '30px',
  padding: '20px',
  backgroundColor: '#fff',
  borderRadius: '16px',
};

const orTextStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: '700',
  color: NETFLIX_COLORS.textSecondary,
  textAlign: 'center',
  margin: '0 0 16px 0',
  letterSpacing: '1px',
  textTransform: 'uppercase',
};

const pixCodeBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  backgroundColor: NETFLIX_COLORS.background,
  padding: '12px',
  borderRadius: '12px',
  marginBottom: '20px',
  border: `1px solid ${NETFLIX_COLORS.border}`,
  flexWrap: 'wrap',
};

const pixCodeLeftStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
};

const pixCodeLabelNewStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: '600',
  color: NETFLIX_COLORS.textSecondary,
  margin: 0,
  textTransform: 'uppercase',
};

const pixCodeValueNewStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: '600',
  color: NETFLIX_COLORS.primary,
  margin: '4px 0 0 0',
  fontFamily: 'monospace',
};

const copyButtonNewStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  backgroundColor: NETFLIX_COLORS.primary,
  color: NETFLIX_COLORS.white,
  padding: '8px 12px',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontWeight: '600',
  fontSize: '11px',
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

const timerBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '12px',
  backgroundColor: `${NETFLIX_COLORS.primary}10`,
  borderRadius: '8px',
  marginBottom: '16px',
};

const timerTextStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: '600',
  color: NETFLIX_COLORS.primary,
};

const bottomHintStyle: React.CSSProperties = {
  fontSize: 12,
  color: NETFLIX_COLORS.textSecondary,
  textAlign: 'center',
  margin: 0,
  lineHeight: '1.5',
};

const loaderSpinStyle: React.CSSProperties = {
  animation: 'spin 1s linear infinite',
  marginBottom: '20px',
};

const successTitleNewStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: '700',
  color: NETFLIX_COLORS.textPrimary,
  margin: 0,
};

const qrcodeImageStyle: React.CSSProperties = {
  width: '220px',
  height: '220px',
  borderRadius: '12px',
};

const manualCheckContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  marginBottom: '16px',
};

const manualCheckButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '12px 20px',
  backgroundColor: NETFLIX_COLORS.primary,
  color: NETFLIX_COLORS.white,
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '600',
  fontSize: '14px',
  transition: 'all 0.3s ease',
};

const checkCounterStyle: React.CSSProperties = {
  fontSize: 12,
  color: NETFLIX_COLORS.textSecondary,
  textAlign: 'center',
  margin: 0,
};
